import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "optimizer_dismiss_low_bar_warning";
const THRESHOLD_STORAGE_KEY = "optimizer_low_bar_threshold";
const DEFAULT_MIN_BARS = 500;

/**
 * Get the user-configured minimum bar threshold (or default)
 */
export function getMinBarThreshold(): number {
  try {
    const stored = localStorage.getItem(THRESHOLD_STORAGE_KEY);
    if (stored) {
      const value = parseInt(stored, 10);
      if (!isNaN(value) && value > 0) {
        return value;
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return DEFAULT_MIN_BARS;
}

/**
 * Set the minimum bar threshold
 */
export function setMinBarThreshold(value: number): void {
  try {
    if (value > 0) {
      localStorage.setItem(THRESHOLD_STORAGE_KEY, value.toString());
    }
  } catch {
    // Ignore localStorage errors
  }
}

interface LowBarCountWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  barCount: number;
  onProceed: () => void;
  onCancel: () => void;
}

/**
 * Check if user has previously dismissed the low bar count warning
 */
export function hasUserDismissedLowBarWarning(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Check if the bar count is below the configured minimum threshold
 */
export function isBarCountLow(barCount: number): boolean {
  return barCount < getMinBarThreshold();
}

/**
 * Alias for getMinBarThreshold - returns the user-configured (or default) threshold
 */
export function getRecommendedMinBars(): number {
  return getMinBarThreshold();
}

export function LowBarCountWarningDialog({
  open,
  onOpenChange,
  barCount,
  onProceed,
  onCancel,
}: LowBarCountWarningDialogProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Reset checkbox when dialog opens
  useEffect(() => {
    if (open) {
      setDontShowAgain(false);
    }
  }, [open]);

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const handleProceed = () => {
    // Save preference if checkbox is checked
    if (dontShowAgain) {
      try {
        localStorage.setItem(STORAGE_KEY, "true");
      } catch {
        // Ignore localStorage errors
      }
    }
    onProceed();
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className={cn(
                  "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg",
                  "bg-dark-900 backdrop-blur-xl border border-dark-700 rounded-xl shadow-2xl",
                )}
                initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                transition={{ type: "spring", duration: 0.3 }}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-amber-500/20 text-amber-500">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <DialogPrimitive.Title className="text-lg font-semibold mb-2">
                        Limited Data Available
                      </DialogPrimitive.Title>
                      <DialogPrimitive.Description className="text-sm text-muted-foreground">
                        Your file contains only <span className="font-semibold text-amber-400">{barCount.toLocaleString()}</span> bars.
                        We recommend at least <span className="font-semibold text-white">{getMinBarThreshold().toLocaleString()}</span> bars
                        for a high-quality optimization study.
                      </DialogPrimitive.Description>
                      <div className="mt-3 p-3 rounded-lg bg-dark-800/50 border border-dark-700">
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium text-white">Tip:</span> In TradingView, try zooming out your chart
                          to load more historical bars before exporting. The more data visible on your screen,
                          the more bars will be included in your export.
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-3">
                        With limited data, the optimizer may not find statistically significant patterns,
                        and results may be less reliable.
                      </p>
                    </div>
                    <DialogPrimitive.Close asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </DialogPrimitive.Close>
                  </div>

                  {/* Don't show again checkbox */}
                  <div className="mt-5 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="dont-show-again"
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      className="h-4 w-4 rounded border-dark-600 bg-dark-800 text-brand-purple focus:ring-brand-purple focus:ring-offset-dark-900"
                    />
                    <label
                      htmlFor="dont-show-again"
                      className="text-sm text-muted-foreground cursor-pointer select-none"
                    >
                      Don't show this warning again
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      className="bg-dark-800/50 border-dark-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleProceed}
                      className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600"
                    >
                      Proceed Anyway
                    </Button>
                  </div>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
